/* eslint-disable no-underscore-dangle */
import KEY_CODES from 'common/KeyCodes';
import { CONSTANTS, isActionColumn } from './GridHelpers';

const { ROW_ACTION_COLID, SELECTION_COLID, ROW_ACTION_BUTTON_CLASS } = CONSTANTS;

const {
  ARROW_DOWN, ARROW_UP, ARROW_LEFT, ARROW_RIGHT, TAB, ENTER_KEY, C, V
} = KEY_CODES;

const isWindows = navigator.userAgent.indexOf('Win') !== -1;

const shouldFocusRowAction = (event) => {
  if (isWindows) {
    return event.key === ARROW_RIGHT && event.altKey;
  }
  return event.key === TAB && event.altKey;
};

const tabToNextCell = (params, gridRef) => {
  if (!params.nextCellPosition) {
    if (params.backwards) {
      const rowAction = gridRef.eGridDiv.querySelector(`.ag-header-cell.${ROW_ACTION_BUTTON_CLASS} button`);
      if (rowAction) {
        setTimeout(() => rowAction.focus());
      }
    }
    // eslint-disable-next-line no-param-reassign
    params.previousCellPosition.column.context.lastCell = true;
  } else {
    // eslint-disable-next-line no-param-reassign
    params.previousCellPosition.column.context.lastCell = false;
  }
  return params.nextCellPosition;
};

const navigateToNextCell = (params) => {
  if (params.event.key === ARROW_DOWN && isActionColumn(params.previousCellPosition.column)) {
    return params.previousCellPosition;
  }
  return params.nextCellPosition;
};

const handleCopyAndPaste = async (params, callback) => {
  /* handle copying */
  if (params.event.key === C && (params.event.ctrlKey || params.event.metaKey)) {
    const text = params.data[params.column.colId];
    // check if the value is defined. For example value for row action column is undefined.
    if (text) {
      // use clipboard API when it is available. Otherwise use root clipboardData element attribute.
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
          /* clipboard successfully set */
        }, () => {
          /* clipboard write failed */
        });
      } else {
        const gridRoot = params.event.target.closest('.ag-root-wrapper');
        gridRoot && gridRoot.setAttribute('clipboardData', text);
      }
    }
  }
  /* handle pasting */
  if (params.event.key === V && (params.event.ctrlKey || params.event.metaKey)) {
    // check if cell is editable
    if (params.colDef.editable === true) {
      const focusedCell = params.api.getFocusedCell();
      // Active editable cell
      params.api.startEditingCell({
        rowIndex: focusedCell.rowIndex,
        colKey: focusedCell.column.colId
      });

      // use clipboard API when it is available. Otherwise use root clipboardData element attribute.
      let clipboardContent = '';
      if (navigator.clipboard) {
        clipboardContent = await navigator.clipboard.readText();
      } else {
        const gridRoot = document.querySelector('.ag-root-wrapper');
        clipboardContent = gridRoot && gridRoot.getAttribute('clipboardData');
      }
      // pass promise to callback
      const pastePromise = new Promise((resolve) => {
        resolve(() => {
          const focusRowNode = params.api.getRowNode(focusedCell.rowIndex);
          const instanceParams = {
            columns: [`${focusedCell.column.colId}`],
            rows: [`${focusRowNode}`]
          };
          const cellEditorInstances = params.api.getCellEditorInstances(instanceParams);
          if (cellEditorInstances && focusRowNode && cellEditorInstances.length > 0) {
            const instance = cellEditorInstances[0];
            if (clipboardContent) {
              instance.componentInstance.setValue(clipboardContent);
            }
          }
        });
      });
      // the callback will be called when the editable cell is active
      callback && callback(pastePromise);
    }
  }
};
const handleCellKeyDown = (params, callback) => {
  // Configure TAB + alt as shortcut to focus row actions
  if (shouldFocusRowAction(params.event) && params.columnApi.getColumn(ROW_ACTION_COLID)) {
    const instanceParams = {
      columns: [`${ROW_ACTION_COLID}`],
      rows: [`${params.node.rowIndex}`]
    };
    const cellRendererInstances = params.api.getCellRendererInstances(instanceParams);
    const instance = cellRendererInstances.find(i => i.params.node.rowIndex === params.node.rowIndex);
    if (instance && instance.componentInstance) {
      params.api.setFocusedCell(params.node.rowIndex, ROW_ACTION_COLID);
    }
  }
  if (!params.disableSingleCellCopy) {
    handleCopyAndPaste(params, callback).then(() => {
      /* clipboard successfully set */
    }, () => {
      /* clipboard write failed */
    });
  }
};

const handleHeaderNavigation = (params, gridRef) => {
  if (
    !params.nextHeaderPosition ||
    (isActionColumn(params.nextHeaderPosition.column) && params.nextHeaderPosition.headerRowIndex === -1)
  ) {
    return null;
  }
  if (
    isActionColumn(params.nextHeaderPosition.column) &&
    params.headerRowCount === 1 &&
    params.nextHeaderPosition.headerRowIndex > -1
  ) {
    const rowAction = gridRef.eGridDiv.querySelector(`.ag-header-cell.${ROW_ACTION_BUTTON_CLASS} button`);
    if (rowAction) {
      setTimeout(() => rowAction.focus());
    }
  }
  return params.nextHeaderPosition;
};

const suppressKeyboardEvent = (params) => {
  const { key, type } = params.event;
  const allCols = params.columnApi.getAllGridColumns();
  const currentColumn = params.column;
  const currentColIdx = allCols.indexOf(currentColumn);
  let nextColIdx = null;
  if (currentColIdx < allCols.length - 1) {
    nextColIdx = currentColIdx + 1;
  } else {
    nextColIdx = 0;
  }
  const cellsAreEditing = params.api.getEditingCells().length > 0;
  const nextColEditable = allCols[nextColIdx].colDef.editable;
  const tabKeyPressed = key === TAB;
  if (!nextColEditable && tabKeyPressed && cellsAreEditing) {
    params.api.stopEditing();
  }
  if (shouldFocusRowAction(params.event)) {
    params.event.preventDefault();
    return true;
  }
  const arrowKeyPressed = key === ARROW_LEFT || key === ARROW_RIGHT || key === ARROW_UP || key === ARROW_DOWN;
  if (arrowKeyPressed && isActionColumn(currentColumn)) {
    return true;
  }
  if (key === ENTER_KEY && type === 'keydown' && currentColumn.colId === SELECTION_COLID) {
    params.node.setSelected(!params.node.selected);
    return true;
  }
  return false;
};

export { tabToNextCell, navigateToNextCell, handleCellKeyDown, handleHeaderNavigation, suppressKeyboardEvent };
